import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { Loader2, CheckCircle2 } from "lucide-react";

const H = { fontFamily: "'Cabinet Grotesk', sans-serif" };

export default function ResetPasswordPage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState("checking");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get(`/auth/reset-password/verify/${token}`)
      .then(({ data }) => setState(data.valid ? "valid" : "invalid"))
      .catch(() => setState("invalid"));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Please use at least 8 characters.");
    if (password !== confirm) return setError("Those passwords do not match.");
    setSaving(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setState("done");
      setTimeout(() => navigate("/app/login"), 2500);
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : "That reset link is no longer valid.");
    }
    setSaving(false);
  };

  return (
    <div className="min-h-screen bg-[#FAF9F6] flex items-center justify-center px-4" data-testid="reset-password-page">
      <div className="w-full max-w-md bg-white rounded-2xl border border-[#1A1B1A]/[0.06] p-8">
        {state === "checking" && (
          <p className="text-sm text-[#757675] flex items-center gap-2" data-testid="reset-checking">
            <Loader2 className="w-4 h-4 animate-spin" /> Checking your link...
          </p>
        )}

        {state === "invalid" && (
          <div data-testid="reset-invalid">
            <h1 className="text-2xl text-[#1A1B1A] mb-2" style={H}>This link has expired</h1>
            <p className="text-sm text-[#4A4B4A] mb-6">
              Reset links can only be used once and last 60 minutes. Please request a new one.
            </p>
            <Link to="/app/forgot-password" data-testid="reset-request-again"
              className="inline-block px-4 py-2.5 rounded-xl bg-[#205B5A] text-white text-sm">
              Request a new link
            </Link>
          </div>
        )}

        {state === "done" && (
          <div data-testid="reset-success">
            <CheckCircle2 className="w-8 h-8 text-[#205B5A] mb-3" />
            <h1 className="text-2xl text-[#1A1B1A] mb-2" style={H}>Password updated</h1>
            <p className="text-sm text-[#4A4B4A]">Taking you to sign in...</p>
          </div>
        )}

        {state === "valid" && (
          <form onSubmit={submit}>
            <h1 className="text-2xl text-[#1A1B1A] mb-2" style={H}>Choose a new password</h1>
            <p className="text-sm text-[#4A4B4A] mb-6">
              This signs you out of any other devices for safety.
            </p>
            <label className="text-xs text-[#757675]">New password</label>
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="reset-password-input" autoComplete="new-password"
              className="mt-1 mb-4 w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm" />
            <label className="text-xs text-[#757675]">Confirm new password</label>
            <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
              data-testid="reset-confirm-input" autoComplete="new-password"
              className="mt-1 mb-4 w-full px-3 py-2.5 rounded-xl border border-[#1A1B1A]/10 text-sm" />
            {error && <p className="text-xs text-red-600 mb-3" data-testid="reset-error">{error}</p>}
            <button type="submit" disabled={saving} data-testid="reset-submit-btn"
              className="w-full px-4 py-2.5 rounded-xl bg-[#205B5A] text-white text-sm disabled:opacity-50">
              {saving ? "Saving..." : "Update password"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
